import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import ApiService from "services/ApiService";
import queryStringService from "services/QueryStringService";
import searchService from "services/SearchService";
import histogramService from "services/HistogramService";
import customURLSearchParams from "services/CustomURLSearchParams";
import {
  errorMessageAtomString,
  filtersAtomObject,
  filtersCommuteAtomObject,
  filtersLoadingAtomBool,
  isLoadingAtomBool,
  isLoadingSkeletonAtomBool,
  originalDataAtomObject,
  originalFiltersAtomObject,
  searchBarFiltersAtomObject,
  searchPerformedAtomBool
} from "../state";
import ObjectChecker from "../utils/ObjectChecker";
import useErrorLogging from "./useErrorLogging";

const useSearchAndFilter = () => {
  const errorLogger = useErrorLogging();

  const [originalData, setOriginalData] = useAtom(originalDataAtomObject);

  const [originalFilters, setOriginalFilters] = useAtom(
    originalFiltersAtomObject
  );

  const [, setCommuteFilters] = useAtom(filtersCommuteAtomObject);
  const [searchBarFilters, setSearchBarFilters] = useAtom(
    searchBarFiltersAtomObject
  );
  const [, setErrorMessage] = useAtom(errorMessageAtomString);

  const [filters, setFilters] = useAtom(filtersAtomObject);

  const [, setFiltersLoading] = useAtom(filtersLoadingAtomBool);
  const [, setSearchPerformed] = useAtom(searchPerformedAtomBool);
  const [, setIsLoading] = useAtom(isLoadingAtomBool);
  const [, setIsLoadingSkeleton] = useAtom(isLoadingSkeletonAtomBool);

  const vennture_dynamic_jobsearch = document.querySelector(
    "#vennture_dynamic_jobsearch"
  );

  const locationFilterValue = vennture_dynamic_jobsearch.getAttribute(
    "data-filter-location"
  );
  
  const radiusFilterValue = vennture_dynamic_jobsearch.getAttribute("data-filter-radius");

  const keywordFilterValue = vennture_dynamic_jobsearch.getAttribute(
    "data-filter-keyword"
  );

  const setQueryString = () => {
    const params = customURLSearchParams();

    queryStringService.setQueryString(params, filters, originalFilters);

    window.history.pushState({}, "", window.location.pathname + "?" + params);
  };

  const search = (token) => {
    if (originalFilters.histograms === undefined) return;

    setIsLoading(token ? false : true);
    setIsLoadingSkeleton(true);

    var requestBody = searchService.getRequest(
      filters,
      originalFilters,
      token,
      locationFilterValue,
      radiusFilterValue,
      keywordFilterValue
    );

    setQueryString();
    setErrorMessage("");

    ApiService.search(requestBody)
      .then((response) => {
        if (token) {
          const updatedJobs = [...originalData.jobs, ...response.jobs];
          setOriginalData({
            jobs: updatedJobs,
            totalCount: response.totalCount,
            histograms: response.histograms,
            nextPageToken: response.nextPageToken,
            isCareers: response.isCareers
          });
        } else {
          setOriginalData(response);
        }
        return response;
      })
      .then((response) => {
        histogramService
          .process(originalFilters, response.histograms)
          .then((filters) => {
            handleFilterDataChange(filters);
          });
      })
      .catch((err) => {
        errorLogger.logError(err);
      })
      .finally(() => {
        setSearchPerformed(true);
        setIsLoadingSkeleton(false);
        setIsLoading(false);
      });
  };

  const handleFilterChange = (filters) => {
    if (new ObjectChecker().IsEmpty(filters)) {
      search();
      return;
    }
    let newFilters = { ...filters };
    setFilters(newFilters);
    search();
  };

  const handleFilterByCustomStringAttributeX = (params, type, key) => {
    if (
      originalFilters &&
      originalFilters[type] &&
      originalFilters[type].slug
    ) {
      var filtered = filters[type]?.find((item) => item?.id === key);

      if (!filtered) return;

      filters[type] = filters[type]?.filter((item) => {
        return item?.id !== filtered.id;
      });

      var originalFilter = originalFilters[type]?.result.find(
        (item) => item?.id === key
      );

      if (originalFilter) originalFilter.active = false;

      const stringQueryValues =
        params.get(originalFilters[type].slug)?.split(",") || [];

      const updatedStringQueryValues = stringQueryValues.filter(
        (id) => id !== key
      );

      if (updatedStringQueryValues.length > 0) {
        params.set(
          originalFilters[type].slug,
          updatedStringQueryValues.join(",")
        );
      } else {
        params.delete(originalFilters[type].slug);
      }

      window.history.pushState({}, "", window.location.pathname + "?" + params);
    }
  };

  const handleClearFilter = (type, key) => {
    const params = customURLSearchParams();
    switch (type) {
      case "type": {
        const typeKey = key;
        filters.jobTypes = filters.jobTypes?.filter((type) => {
          return type?.key !== typeKey;
        });
        var foundType = originalFilters.jobTypes?.find(
          (jobType) => jobType?.key === typeKey
        );
        if (foundType) foundType.active = false;

        const typeKeys = params.get(type)?.split(",") || [];
        const updatedTypeKeys = typeKeys.filter((id) => id !== typeKey);
        if (updatedTypeKeys.length > 0) {
          params.set(type, updatedTypeKeys.join(","));
        } else {
          params.delete(type);
        }
        window.history.pushState(
          {},
          "",
          window.location.pathname + "?" + params
        );

        setSearchBarFilters({
          ...searchBarFilters,
          jobTypes: null,
        });

        break;
      }
      case "sector": {
        const sectorId = key;
        filters.customStringAttributes = filters.customStringAttributes?.filter(
          (sector) => {
            return sector?.id !== sectorId;
          }
        );
        var foundSource =
          originalFilters.customStringAttributes[0]?.result.find(
            (sector) => sector?.id === sectorId
          );

        if (foundSource) foundSource.active = false;
        const sectorIds = params.get(type)?.split(",") || [];
        const updatedSectorIds = sectorIds.filter((id) => id !== sectorId);
        if (updatedSectorIds.length > 0) {
          params.set(type, updatedSectorIds.join(","));
        } else {
          params.delete(type);
        }
        window.history.pushState(
          {},
          "",
          window.location.pathname + "?" + params
        );
        break;
      }
      case "customStringAttributes": {
        handleFilterByCustomStringAttributeX(params, type, key);
        break;
      }
      case "customStringAttributes1": {
        handleFilterByCustomStringAttributeX(params, type, key);
        break;
      }
      case "customStringAttributes2": {
        handleFilterByCustomStringAttributeX(params, type, key);
        break;
      }
      case "customStringAttributes3": {
        handleFilterByCustomStringAttributeX(params, type, key);
        break;
      }
      case "customStringAttributes4": {
        handleFilterByCustomStringAttributeX(params, type, key);
        break;
      }
      case "commute": {
        filters.commute = {};
        originalFilters.commute = filters.commute;
        // Remove parameters from the query string
        params.delete(type);
        window.history.pushState(
          {},
          "",
          window.location.pathname + "?" + params
        );

        break;
      }
      case "queryString": {
        filters.queryString = "";
        originalFilters.queryString = filters.queryString;
        // Remove parameters from the query string
        params.delete(type);
        window.history.pushState(
          {},
          "",
          window.location.pathname + "?" + params
        );
        setSearchBarFilters({
          ...searchBarFilters,
          keyword: "",
        });

        break;
      }

      case "location": {
        filters.location = "";
        filters.distance = "";
        originalFilters.location = filters.location;
        originalFilters.distance = filters.distance;
        // Remove parameters from the query string
        params.delete("location");
        params.delete("distance");
        window.history.pushState(
          {},
          "",
          window.location.pathname + "?" + params
        );
        setSearchBarFilters({
          ...searchBarFilters,
          location: "",
          locationSearchTerm: "",
          distance: 10,
        });

        break;
      }
      case "remote": {
        filters.remoteWorking = false;
        originalFilters.remoteWorking = false;
        break;
      }
      case "salary": {
        filters.salary = null;
        originalFilters.salary = filters.salary;
        // Remove parameters from the query string
        params.delete(type);
        window.history.pushState(
          {},
          "",
          window.location.pathname + "?" + params
        );

        break;
      }
      default:
        break;
    }

    handleFilterDataChange(originalFilters);

    handleFilterChange(filters);
  };



  const [initialSearch, setInitialSearch] = useState(false);

  useEffect(() => {
    if (initialSearch === true) return;

    if (originalFilters.histograms) {
      search();
      setInitialSearch(true);
    }
  }, [originalFilters.histograms]);

  // handle filter changes
  useEffect(() => {
    setOriginalData({
      jobs: [],
      totalCount: 0,
    });
    handleFilterChange(filters);
  }, [
    filters.commute,
    filters.queryString,
    filters.jobTypes,
    filters.location,
    filters.distance,
    filters.customStringAttributes1,
    filters.customStringAttributes2,
    filters.customStringAttributes3,
    filters.customStringAttributes4,
    filters.salary,
    filters.remoteWorking,
  ]);


  const getQueryString = (filterData) => {
    const params = customURLSearchParams();

    queryStringService.getQueryString(params, filters, filterData);

    if (
      filters.commute?.startingLocation &&
      filters.commute?.method &&
      filters.commute?.travelTime
    ) {
      setCommuteFilters({
        commuteLocation: { name: filters.commute?.startingLocation?.name },
        commuteSearchTerm: { name: filters.commute?.startingLocation?.name },
        commuteMethod: filters.commute.method,
        commuteTime: {
          name: filters.commute.travelTime,
          id: parseInt(filters.commute.travelTime),
        },
      });
    }

    handleFilterDataChange(filterData);

    handleFilterChange(filters);
  };

  const handleFilterDataChange = (filterData) => {
    if (new ObjectChecker().IsEmpty(filterData)) return;
    let newFilterData = { ...filterData };
    setOriginalFilters(newFilterData);
  };

  const initialiseFilters = (data) => {
    setFilters({
      queryString: "",
      location: "",
      distance: 0,
      histograms: data.histograms,
      commute: { method: {}, startingLocation: {} },
      remoteWorking: false,
      jobTypes: [],
      sectors: [],
      customStringAttributes: [],
      customStringAttributes1: [],
      customStringAttributes2: [],
      customStringAttributes3: [],
      customStringAttributes4: [],
      salary: { type: {} },
    });
  };

  // intial useEffect load

  useEffect(() => {
    setFiltersLoading(true);
    ApiService.filters()
      .then((response) => {
        handleFilterDataChange(response);
        return response;
      })
      .then((data) => {
        initialiseFilters(data);
        return data;
      })
      .then((data) => {
        // data.commute = {
        //   method: { id: 0 },
        //   startingLocation: { latlng: { lat: 0, lng: 0 } },
        //   travelTime: 0,
        // };
        getQueryString(data);
      })
      .catch((err) => errorLogger.logError(err))
      .finally(() => {
        setFiltersLoading(false);
      });
  }, []);

  // exposed functions
  return {
    getQueryString,
    search,
    handleFilterChange,
    handleClearFilter,
    handleFilterByCustomStringAttributeX,
  };
};

export default useSearchAndFilter;
